<script setup lang="ts">
import type { CompositionBackgroundCardShadow, UIColor } from "@align/ui";
import type { RouteLocationRaw } from "vue-router";
import { useLayoutStore } from "~/composables/stores/layout";

const {
  label = "",
  byline = "",
  shadow = "none",
  colorForeground = "--color-primary",
  colorBackground = "--color-white",
  colorByline = "--color-accent-500",
} = defineProps<{
  label?: string;
  byline?: string;
  colorBackground?: UIColor;
  colorForeground?: UIColor;
  colorByline?: UIColor;
  omitPadding?: boolean;
  omitPaddingTop?: boolean;
  omitPaddingRight?: boolean;
  omitPaddingBottom?: boolean;
  omitPaddingLeft?: boolean;
  omitPaddingContent?: boolean;
  justifyTextsCenter?: boolean;
  justifyContentCenter?: boolean;
  justifyContentEnd?: boolean;
  alignItemsCenter?: boolean;
  alignItemsEnd?: boolean;
  paddingDouble?: boolean;
  loadingEnabled?: boolean;
  loading?: boolean;
  rounded?: boolean;
  row?: boolean;
  clickMoreLabel?: string;
  shadow?: CompositionBackgroundCardShadow;
  explodeHeight?: boolean;
  /** works with explodeHeight, to make sure content does not expand the container */
  clipContent?: boolean;
  onWidgetClickMore?: () => void;
  /** optional route; if provided, wraps the CTA in a NuxtLink */
  clickMoreRoute?: RouteLocationRaw;
}>();

const t = useTranslator();

const layoutStore = useLayoutStore();

const widgetScroll = layoutStore.widgetScroll;
</script>

<template>
  <Composition explodeHeight class="widget-widget-blank">
    <template #backgrounds>
      <CompositionBackgroundCard :color="colorBackground" :rounded="rounded" :shadow="shadow" />
    </template>

    <template #layouts>
      <Composition explodeHeight :loadingEnabled="loadingEnabled" :loading="loading" loadingColor="--color-accent-200">
        <template #layouts>
          <CompositionLayoutSingle omitPadding>
            <template #container>
              <div
                :class="[
                  $style.layout,
                  widgetScroll && $style.widgetScroll,
                  paddingDouble && $style.paddingDouble,
                  omitPadding && $style.omitPadding,
                  omitPaddingTop && $style.omitPaddingTop,
                  omitPaddingRight && $style.omitPaddingRight,
                  omitPaddingBottom && $style.omitPaddingBottom,
                  omitPaddingLeft && $style.omitPaddingLeft,
                  omitPaddingContent && !onWidgetClickMore && $style.omitPaddingBottom,
                  omitPaddingContent && onWidgetClickMore && $style.omitPaddingBottomClickMore,
                  explodeHeight && $style.explodeHeight,
                  clipContent && $style.clipContent,
                ]"
              >
                <div
                  v-if="label.length > 0 || byline.length > 0 || $slots.controls"
                  :class="[$style.texts, byline.length === 0 && $style.textsOmitPaddingBottom]"
                >
                  <div :class="$style.header">
                    <div
                      v-if="label.length > 0"
                      :class="[$style.label, byline.length === 0 && $style.textsOmitPaddingBottom, justifyTextsCenter && $style.justifyTextsCenter]"
                    >
                      <Copy type="label" size="small" :color="colorForeground">
                        {{ label }}
                      </Copy>
                    </div>

                    <div v-if="$slots.controls">
                      <slot name="controls" />
                    </div>
                  </div>

                  <div v-if="byline.length > 0" :class="[$style.byline, justifyTextsCenter && $style.justifyTextsCenter]">
                    <Copy type="body" size="small" :color="colorByline" wrap>
                      {{ byline }}
                    </Copy>
                  </div>
                </div>

                <div
                  :class="[
                    $style.slot,
                    row && $style.row,
                    widgetScroll && $style.widgetScroll,
                    justifyContentCenter && $style.justifyContentCenter,
                    justifyContentEnd && $style.justifyContentEnd,
                    alignItemsEnd && $style.alignItemsEnd,
                    alignItemsCenter && $style.alignItemsCenter,
                    omitPaddingContent && $style.omitPaddingContent,
                    explodeHeight && $style.explodeHeight,
                    clipContent && $style.clipContent,
                  ]"
                >
                  <slot name="container" />
                </div>

                <!-- “See More” CTA: prefer NuxtLink when route is passed -->
                <div v-if="clickMoreRoute" :class="$style.cta">
                  <NuxtLink :to="clickMoreRoute">
                    <Cta :color="colorForeground" :label="clickMoreLabel ?? t('OVERVIEW_DASHBOARD_SEE_MORE')" />
                  </NuxtLink>
                </div>
                <div v-else-if="onWidgetClickMore" :class="$style.cta">
                  <Cta :color="colorForeground" :label="clickMoreLabel ?? t('OVERVIEW_DASHBOARD_SEE_MORE')" @click="onWidgetClickMore" />
                </div>
              </div>
            </template>
          </CompositionLayoutSingle>
        </template>
      </Composition>
    </template>
  </Composition>
</template>

<style module>
.layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem;
}

.layout:not(.explodeHeight) {
  height: 100%;
}

.layout.omitPadding {
  padding: 0;
}

.layout.omitPaddingTop {
  padding-top: 0;
}

.layout.omitPaddingRight {
  padding-right: 0;
}

.layout.omitPaddingBottom {
  padding-bottom: 0;
}

.layout.omitPaddingLeft {
  padding-left: 0;
}

.layout.paddingDouble {
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.header {
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
}
.texts {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0.5rem;
}

.justifyTextsCenter {
  justify-content: center;
}

.label,
.byline {
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-bottom: 0.5rem;
}

.textsOmitPaddingBottom {
  margin-bottom: 0;
}

.slot {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.slot.widgetScroll {
  height: 100%;
}

.explodeHeight {
  flex: 1;
}

.clipContent {
  min-height: 0;
}

.slot.row {
  flex-direction: row;
}

.slot.justifyContentCenter {
  justify-content: center;
}

.slot.justifyContentEnd {
  justify-content: flex-end;
}

.slot.alignItemsCenter {
  align-items: center;
}

.slot.alignItemsEnd {
  align-items: flex-end;
}
.slot.omitPaddingContent {
  width: calc(100% + 2rem);
  transform: translateX(-1rem);
}

.slot.omitPaddingBottomClickMore {
  width: calc(100% + 2rem);
  transform: translateX(-1rem);
  outline: solid 2px red;
}

.cta {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-end;
  padding-top: 0.5rem;
}
</style>
